import { SkeletonLoader } from '@toggle/design-system';
import { PriceStatus } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceDisplay } from '~/react-query-hooks/prices/usePriceDisplay';

import * as S from './InsightsLivePriceChange.styles';

export interface InsightsLivePriceChangeProps {
  entity: Entity;
  isWindowComplete?: boolean;
  proportionChange?: number;
}

export const InsightsLivePriceChange = ({
  entity,
  proportionChange,
  isWindowComplete,
}: InsightsLivePriceChangeProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  const proportionSign =
    proportionChange !== undefined && proportionChange >= 0 ? '+' : '-';
  const proportionStatus =
    proportionChange !== undefined && proportionChange >= 0
      ? PriceStatus.Positive
      : PriceStatus.Negative;

  if (isLoading) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    if (proportionChange !== undefined) {
      return (
        <div data-testid="insight-live-price-change">
          <S.PercentageChangeLabel
            $status={proportionStatus}
          >{`${proportionSign}${Math.abs(proportionChange).toFixed(
            2
          )}%`}</S.PercentageChangeLabel>
        </div>
      );
    }

    return null;
  }

  const { price } = priceData;
  const { marketHours } = price.primary;

  const hasLiveSecondaryPrice =
    !isWindowComplete &&
    price.secondary?.price !== undefined &&
    price.secondary?.isLive &&
    price.secondary.priceChange;

  const hasLivePrimaryPrice =
    !isWindowComplete &&
    marketHours.isMarketHours &&
    price.primary?.isLive &&
    price.primary.priceChange;

  const livePriceData =
    marketHours.isPreMarket && hasLiveSecondaryPrice
      ? {
          priceChange: price.secondary?.priceChange,
          label: t('analyze:analysis.preMarket'),
        }
      : marketHours.isAfterMarket && hasLiveSecondaryPrice
      ? {
          priceChange: price.secondary?.priceChange,
          label: t('analyze:analysis.after-hours'),
        }
      : hasLivePrimaryPrice
      ? {
          priceChange: price.primary.priceChange,
          label: t('analyze:analysis.marketOpen'),
        }
      : null;

  return (
    <div data-testid="insight-live-price-change">
      <>
        {livePriceData?.priceChange ? (
          <>
            <S.PercentageChangeLabel $status={livePriceData.priceChange.status}>
              {livePriceData.priceChange.proportionChange}
            </S.PercentageChangeLabel>
          </>
        ) : (
          proportionChange !== undefined && (
            <S.PercentageChangeLabel
              $status={proportionStatus}
            >{`${proportionSign}${Math.abs(proportionChange).toFixed(
              2
            )}%`}</S.PercentageChangeLabel>
          )
        )}
      </>
    </div>
  );
};
